import {usePaginationFragment} from 'react-relay'

import {getInitialTimelinePageSize} from '../../constants/values'
import type {IssueTimelineIssueFragment$data} from './__generated__/IssueTimelineIssueFragment.graphql'
import type {NewTimelinePaginationBackQuery} from './documents/__generated__/NewTimelinePaginationBackQuery.graphql'
import type {NewTimelinePaginationFrontQuery} from './documents/__generated__/NewTimelinePaginationFrontQuery.graphql'
import type {useTimelineItemsBackFragment$key} from './documents/__generated__/useTimelineItemsBackFragment.graphql'
import type {useTimelineItemsFrontFragment$key} from './documents/__generated__/useTimelineItemsFrontFragment.graphql'
import {backTimelineItemsFragment} from './documents/use-timeline-items-back-fragment'
import {backTimelineItemsQuery} from './documents/use-timeline-items-back-query'
import {frontTimelineItemsFragment} from './documents/use-timeline-items-front-fragment'
import type {TimelineFeed} from './fetch-timeline-items'
import {createBackFetcherConfig, useTimelineItemsCore} from './use-timeline-items-core'

type UseTimelineItemsProps = {
  timelineData: IssueTimelineIssueFragment$data
  feed?: TimelineFeed
  highlightedEvent: string | undefined
  page?: number
}

const classicBackFetcherConfig = createBackFetcherConfig(backTimelineItemsQuery, 'backTimelineItems')
const commentsBackFetcherConfig = createBackFetcherConfig(backTimelineItemsQuery, 'backTimelineItems', {
  itemTypes: ['ISSUE_COMMENT'],
})

/**
 * Classic (unfiltered) timeline hook.
 *
 * Reads from the classic front/back fragments and delegates pagination,
 * highlight-loading, and timeline stitching to the shared core hook.
 */
export const useTimelineItems = ({timelineData, feed = 'activity', highlightedEvent, page}: UseTimelineItemsProps) => {
  const {
    data: {frontTimelineItems: frontTimelineData},
    loadNext: loadMoreFrontItems,
  } = usePaginationFragment<NewTimelinePaginationFrontQuery, useTimelineItemsFrontFragment$key>(
    frontTimelineItemsFragment,
    timelineData,
  )

  const {
    data: {backTimelineItems: backTimelineData},
  } = usePaginationFragment<NewTimelinePaginationBackQuery, useTimelineItemsBackFragment$key>(
    backTimelineItemsFragment,
    timelineData,
  )

  return useTimelineItemsCore({
    issueId: timelineData.id,
    frontData: frontTimelineData,
    backData: backTimelineData,
    loadMoreFrontItems,
    backFetcherConfig: feed === 'comments' ? commentsBackFetcherConfig : classicBackFetcherConfig,
    timelineItemTypes: feed === 'comments' ? ['ISSUE_COMMENT'] : undefined,
    initialBackPageSize: getInitialTimelinePageSize(),
    highlightedEvent,
    page,
  })
}
